if (!customElements.get('ghc-honey-purchase')) {
  customElements.define('ghc-honey-purchase', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.variants = JSON.parse(this.querySelector('[data-honey-variants]').textContent);
      this.words = JSON.parse(this.querySelector('[data-honey-strings]').textContent);
      this.rows = [...this.querySelectorAll('[data-honey-row]')];
      this.controls = this.querySelector('[data-honey-controls]');
      this.add = this.querySelector('[data-honey-add]');
      this.checkout = this.querySelector('[data-honey-checkout]');
      this.error = this.querySelector('[data-honey-error]');
      this.status = this.querySelector('[data-honey-status]');
      this.preview = this.dataset.mode !== 'live';
      this.maxJars = Number(this.dataset.maxJars || 12);
      this.cartCount = 0;
      this.pending = false;
      this.uncertain = false;
      this.saved = null;
      this.money = new Intl.NumberFormat(this.dataset.locale || 'en-GB', { style: 'currency', currency: this.dataset.currency || 'GBP' });
      this.rows.forEach((row, index) => {
        const input = row.querySelector('[data-honey-quantity]');
        const variant = this.variants[index];
        input.max = String(Math.min(variant.max ?? this.maxJars, this.maxJars));
        input.disabled = !variant.available;
        row.querySelector('[data-honey-minus]').addEventListener('click', () => this.step(index, -1), options);
        row.querySelector('[data-honey-plus]').addEventListener('click', () => this.step(index, 1), options);
        input.addEventListener('input', () => this.sync(), options);
        input.addEventListener('change', () => {
          if (input.value === '') input.value = '0';
          this.sync();
        }, options);
      });
      window.addEventListener('pageshow', event => {
        if (!event.persisted || this.preview) return;
        this.pending = this.uncertain = false;
        this.rows.forEach(row => { row.querySelector('[data-honey-quantity]').value = '0'; });
        this.querySelector('[data-honey-recovery]').hidden = true;
        this.sync();
      }, options);
      this.add.addEventListener('click', () => this.commit(false), options);
      this.checkout.addEventListener('click', () => this.commit(true), options);
      this.controls.disabled = false;
      this.sync();
    }
    disconnectedCallback() { this.controller?.abort(); }
    format(value) { return this.money.format(value / 100); }
    selection() {
      return this.rows.map((row, index) => ({
        ...this.variants[index],
        quantity: row.querySelector('[data-honey-quantity]').value === '' ? NaN : Number(row.querySelector('[data-honey-quantity]').value)
      }));
    }
    step(index, delta) {
      if (this.pending || this.uncertain) return;
      const input = this.rows[index].querySelector('[data-honey-quantity]');
      const variant = this.variants[index];
      if (!variant.available) return;
      const value = Number(input.value);
      const others = this.selection().reduce((sum, item, i) => sum + (i !== index && Number.isSafeInteger(item.quantity) && item.quantity > 0 ? item.quantity : 0), this.cartCount);
      const next = (Number.isInteger(value) && value >= 0 ? value : 0) + delta;
      input.value = String(Math.max(0, Math.min(variant.max ?? Infinity, Math.max(0, this.maxJars - others), next)));
      this.sync();
    }
    sync() {
      const selection = this.selection();
      let message = '';
      const selectedCount = selection.reduce((sum, item) => sum + (Number.isSafeInteger(item.quantity) && item.quantity >= 0 ? item.quantity : 0), 0);
      const orderFull = selectedCount + this.cartCount >= this.maxJars;
      selection.forEach((item, index) => {
        const invalid = !Number.isSafeInteger(item.quantity) || item.quantity < 0;
        const over = !item.available ? item.quantity > 0 : item.max !== null && item.quantity > item.max;
        const row = this.rows[index];
        row.dataset.selected = String(!invalid && item.quantity > 0);
        row.querySelector('input').setAttribute('aria-invalid', String(invalid || over));
        row.querySelector('[data-honey-minus]').disabled = this.pending || this.uncertain || !item.available || item.quantity <= 0;
        row.querySelector('[data-honey-plus]').disabled = this.pending || this.uncertain || !item.available || orderFull || (item.max !== null && item.quantity >= item.max);
        if (!message && invalid) message = this.words.invalid.replace('[apiary]', item.title);
        if (!message && over) message = this.words.limit.replace('[apiary]', item.title).replace('[max]', String(item.max ?? 0));
      });
      if (!message && selectedCount + this.cartCount > this.maxJars) {
        message = this.cartCount ? this.words.basket_limit_error.replace('[count]', String(this.cartCount)).replace('[remaining]', String(Math.max(0, this.maxJars - this.cartCount))) : this.words.order_limit_error;
      }
      this.valid = !message;
      this.error.textContent = message;
      const count = this.valid ? selection.reduce((sum, item) => sum + item.quantity, 0) : 0;
      const total = this.valid ? selection.reduce((sum, item) => sum + item.price * item.quantity, 0) : 0;
      this.querySelector('[data-honey-count]').textContent = !count ? this.words.empty : count === 1 ? this.words.one : this.words.many.replace('[count]', String(count));
      this.querySelector('[data-honey-total]').textContent = this.valid ? this.format(total) : '—';
      this.checkout.disabled = this.pending || this.uncertain || !this.valid || count === 0;
      this.add.disabled = this.pending || this.uncertain || !this.valid || (count === 0 && !(this.preview && this.saved !== null));
      this.controls.disabled = this.pending || this.uncertain;
      const action = count === 1 ? this.words.add_one : this.words.add_many;
      const addLabel = this.querySelector('[data-honey-add-label]');
      addLabel.textContent = count && this.valid ? action.replace('[count]', String(count)).replace('[total]', this.format(total)) : this.words.add;
      if (this.preview && this.saved !== null) {
        const changed = this.saved !== JSON.stringify(selection.map(item => item.quantity));
        this.status.textContent = changed ? this.words.changed : this.words.added;
        addLabel.textContent = changed ? (count ? this.words.update_total.replace('[total]', this.format(total)) : this.words.empty_basket) : this.words.in_basket;
        this.add.disabled = this.add.disabled || !changed;
      }
    }
    async commit(checkout) {
      if (this.pending || this.uncertain) return;
      this.sync();
      const items = this.selection().filter(item => item.quantity > 0);
      if (!this.valid) return;
      if (!items.length) {
        if (this.preview && this.saved !== null && !checkout) {
          this.saved = null;
          this.querySelector('[data-honey-basket]').hidden = true;
          this.querySelector('[data-honey-lines]').replaceChildren();
          this.querySelector('[data-honey-add-label]').textContent = this.words.add;
          this.sync();
          this.status.textContent = this.words.cleared;
        }
        return;
      }
      if (this.preview) {
        const basket = this.querySelector('[data-honey-basket]');
        const lines = this.querySelector('[data-honey-lines]');
        lines.replaceChildren();
        items.forEach(item => {
          const line = document.createElement('li');
          const title = document.createElement('span');
          const cost = document.createElement('span');
          title.textContent = `${item.title} × ${item.quantity}`;
          cost.textContent = this.format(item.price * item.quantity);
          line.append(title, cost);
          lines.append(line);
        });
        const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
        this.querySelector('[data-honey-basket-total]').textContent = this.format(subtotal);
        this.saved = JSON.stringify(this.selection().map(item => item.quantity));
        basket.hidden = false;
        this.sync();
        basket.focus({ preventScroll: true });
        basket.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        return;
      }
      this.pending = true;
      this.sync();
      this.setAttribute('aria-busy', 'true');
      let addStarted = false;
      try {
        const cartResponse = await fetch(this.dataset.cartJsonUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('cart-check');
        const basket = await cartResponse.json();
        if (!Array.isArray(basket.items)) throw new Error('cart-check');
        this.cartCount = basket.items.reduce((sum, item) => sum + (String(item.product_id) === this.dataset.productId ? item.quantity : 0), 0);
        if (this.cartCount + items.reduce((sum, item) => sum + item.quantity, 0) > this.maxJars) {
          this.pending = false;
          this.sync();
          return;
        }
        addStarted = true;
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items.map(item => ({ id: item.id, quantity: item.quantity })) })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('basket');
        if (checkout) {
          const form = this.querySelector('[data-honey-checkout-form]');
          form.requestSubmit(this.querySelector('[data-honey-checkout-submit]'));
        } else window.location.assign(this.dataset.cartUrl);
      } catch {
        if (!addStarted) {
          this.pending = false;
          this.sync();
          this.error.textContent = this.words.cart_check_error;
          return;
        }
        this.uncertain = true;
        this.pending = false;
        this.sync();
        this.error.textContent = this.words.error;
        this.querySelector('[data-honey-recovery]').hidden = false;
        this.querySelector('[data-honey-recovery]').focus();
      } finally { this.removeAttribute('aria-busy'); }
    }
  });
}
